import { useT } from '@hana/i18n';
import { Chip, Table, useToast, type Column } from '@hana/ui';
import { f1, fi, pc } from './data';
import { BarsPanel, Card, Kpis, type BarRow, type Kpi } from './panels';
import { Bubbles, Donut } from './SvcRole';
import s from './bod.module.css';
import './i18nLot';

// Mint-BOD-Lot (Mint-BOD tab=loss lot=low|all): lợi nhuận theo lô. Chỉ dựng sau quyền money (Bod.tsx). Số mẫu chép từ code board.
export type Lot = { job: string; cus: string; typ: string; route: string; sale: string; m: number; rev: number; cost: number; note: string };
export type LotFilter = 'loss' | 'low' | 'all';
export const LOT_FILTERS: readonly LotFilter[] = ['loss', 'low', 'all'];

const lot = ([job, cus, typ, route, sale, m, rev, cost, note]: readonly [string, string, string, string, string, number, number, number, string]): Lot => ({ job, cus, typ, route, sale, m, rev, cost, note });
export const PROFIT_LOTS: readonly Lot[] = ([
  ['SE261040501', 'RUITONGVN-SC', 'OB', 'VNSGN → CNNGB', 'DAVID', 11, 58.4, 49.1, ''],
  ['SE261040488', 'GREENBUD', 'OB', 'VNSGN → KRPUS', 'DAVID', 11, 44.2, 38.0, ''],
  ['SL261040120', 'TAMNGOC', 'LS', 'Cần Thơ → VNCLI', 'PHILIP', 11, 21.6, 18.9, ''],
  ['SE261040433', 'NAMMY', 'OB', 'VNVUT → JPTYO', 'TUANNT', 11, 39.8, 37.9, 'Biên mỏng · cước mua sát giá bán'],
  ['ST261040061', 'QUEKYCORP', 'TR', 'Bình Dương → Cát Lái', 'ANDG', 11, 9.6, 7.4, ''],
  ['SE261040390', 'PHUCUONGEXIM', 'OB', 'VNSGN → CNSHK', 'DAVID', 11, 33.5, 28.2, ''],
  ['SE260939902', 'ARRIVE', 'OB', 'VNHPH → KRINC', 'TUANNT', 10, 26.1, 25.0, 'Biên mỏng · phụ thu mùa cao điểm'],
  ['SE260939811', 'COCOGROUP', 'OB', 'VNSGN → MYPKG', 'AnNPH', 10, 24.7, 20.3, ''],
  ['SI260939540', 'MINHPHAT', 'IB', 'CNSHA → VNSGN', 'MAITTN', 10, 31.0, 26.6, ''],
  ['SE260939477', 'QUEKYCORP', 'OB', 'VNSGN → SAJED', 'ANDG', 10, 71.2, 62.8, ''],
  ['SL260939301', 'TAMNGOC', 'LS', 'Cần Thơ → VNCLI', 'PHILIP', 10, 19.8, 19.1, 'Biên mỏng · chờ xe'],
  ['SE260838960', 'GREENBUD', 'OB', 'VNSGN → KRPUS', 'DAVID', 9, 46.9, 40.2, ''],
  ['ST260838722', 'NAMMY', 'TR', 'Vũng Tàu → Cái Mép', 'TUANNT', 9, 8.8, 6.9, ''],
  ['SE260838655', 'RUITONGVN-SC', 'OB', 'VNSGN → CNNGB', 'DAVID', 9, 52.3, 45.0, ''],
] as const).map(lot);

const mg = (r: Lot) => (r.rev ? ((r.rev - r.cost) / r.rev) * 100 : 0);
const BANDS: readonly (readonly [string, (m: number) => boolean, string])[] = [
  ['loss', (m) => m < 0, 'pr'], ['0', (m) => m >= 0 && m < 5, 'p2'], ['1', (m) => m >= 5 && m < 10, 'py'], ['2', (m) => m >= 10 && m < 15, 'pb'], ['3', (m) => m >= 15, 'p1'],
];

export function LotView({ filter, idx, lossLots }: { filter: Exclude<LotFilter, 'loss'>; idx: readonly number[]; lossLots: readonly Lot[] }) {
  const t = useT();
  const toast = useToast();
  const all = lossLots.concat(PROFIT_LOTS).filter((r) => idx.includes(r.m));
  const rows = (filter === 'low' ? all.filter((r) => mg(r) < 5) : all).slice().sort((a, b) => (filter === 'low' ? mg(a) - mg(b) : b.rev - b.cost - (a.rev - a.cost)));
  const rv = all.reduce((a, r) => a + r.rev, 0), pf = all.reduce((a, r) => a + r.rev - r.cost, 0), avg = rv ? (pf / rv) * 100 : 0;
  const bk = BANDS.map(([k, f, c]) => ({ k: t(`lot.b.${k}`), n: all.filter((r) => f(mg(r))).length, c }));
  const bySale = new Map<string, [number, number, number]>(), byCus = new Map<string, number>();
  all.forEach((r) => {
    const b = bySale.get(r.sale) ?? [0, 0, 0];
    bySale.set(r.sale, [b[0] + r.rev, b[1] + r.rev - r.cost, b[2] + 1]);
    byCus.set(r.cus, (byCus.get(r.cus) ?? 0) + r.rev - r.cost);
  });
  const SL = [...bySale].map(([k, b]) => ({ k, p: b[1], n: b[2], m: b[0] ? (b[1] / b[0]) * 100 : 0 })).sort((a, b) => b.p - a.p);
  const CU = [...byCus].map(([k, p]) => ({ k, p })).sort((a, b) => b.p - a.p).slice(0, 8);
  const kpis: Kpi[] = [
    { k: t('lot.t.n'), v: fi(all.length), sub: t('lot.t.nS') },
    { k: t('lot.t.rev'), v: f1(rv), u: t('lot.unit') },
    { k: t('lot.t.profit'), v: f1(pf), u: t('lot.unit'), tone: pf < 0 ? 'err' : 'ok' },
    { k: t('lot.t.margin'), v: pc(avg) },
    { k: t('lot.t.loss'), v: fi(bk[0]!.n), tone: bk[0]!.n ? 'err' : undefined, sub: t('lot.t.lossS') },
  ];
  const best = all.length ? all.reduce((a, r) => (mg(r) > mg(a) ? r : a), all[0]!) : null;
  const nLow = all.filter((r) => mg(r) < 5).length;
  const ins = best ? [
    t('lot.i.avg', { m: pc(avg), n: all.length, low: nLow, p: pc((nLow / all.length) * 100) }),
    t('lot.i.best', { job: best.job, cus: best.cus, m: pc(mg(best)), sale: best.sale }),
    SL.length ? t('lot.i.sale', { a: SL[0]!.k, av: f1(SL[0]!.p), b: SL[SL.length - 1]!.k, bv: f1(SL[SL.length - 1]!.p) }) : '',
  ].filter(Boolean) : [];
  const saleRows: BarRow[] = SL.map((x) => ({ k: x.k, n: x.p, tone: 'pri', v: f1(x.p), s: t('lot.sale.sub', { n: x.n, m: pc(x.m) }) }));
  const cusRows: BarRow[] = CU.map((x) => ({ k: x.k, n: Math.abs(x.p), tone: x.p < 0 ? 'err' : 'ok', v: f1(x.p) }));
  const cols: Column<Lot>[] = [
    { key: 'job', header: t('bod.m.l.c.job'), width: '12%', cell: (r) => <button type="button" className={s.jobLink} onClick={() => toast({ msg: t('lot.open', { job: r.job }) })}>{r.job}</button> },
    { key: 'cus', header: t('bod.m.l.c.cus'), width: '13%', cell: (r) => r.cus },
    { key: 'typ', header: t('bod.m.l.c.type'), width: '6%', cell: (r) => <Chip tone="info">{r.typ}</Chip> },
    { key: 'route', header: t('bod.m.l.c.route'), width: '14%', cell: (r) => <span className={s.mute}>{r.route}</span> },
    { key: 'sale', header: t('bod.m.l.c.sale'), width: '8%', cell: (r) => <span className={s.mute}>{r.sale}</span> },
    { key: 'rev', header: t('bod.m.c.rev'), width: '9%', align: 'right', cell: (r) => f1(r.rev) },
    { key: 'cost', header: t('bod.m.c.cost'), width: '9%', align: 'right', cell: (r) => f1(r.cost) },
    { key: 'p', header: t('bod.m.c.profit'), width: '9%', align: 'right', cell: (r) => <b className={mg(r) < 0 ? s.down : undefined}>{f1(r.rev - r.cost)}</b> },
    { key: 'm', header: t('lot.c.margin'), width: '7%', align: 'right', cell: (r) => { const m = mg(r); return m < 0 ? <Chip tone="err">{pc(m)}</Chip> : m < 5 ? <Chip tone="warn">{pc(m)}</Chip> : <span className={s.mute}>{pc(m)}</span>; } },
    { key: 'note', header: t('bod.m.l.c.note'), width: '13%', cell: (r) => <span className={s.mute}>{r.note}</span> },
  ];
  const low = filter === 'low';
  return (
    <>
      <Kpis items={kpis} />
      {all.length > 0 && (
        <>
          <Card title={t('lot.k.dist')} sub={t('lot.s.dist')} span={4}><Donut rows={bk.map((x) => [x.k, x.n] as const)} cls={bk.map((x) => x.c)} tot={all.length} unit={t('lot.lots')} /></Card>
          <BarsPanel title={t('lot.k.sale')} sub={t('lot.s.sale')} span={4} rows={saleRows} />
          <BarsPanel title={t('lot.k.cus')} sub={t('lot.s.cus')} span={4} rows={cusRows} />
          <Card title={t('lot.k.sc')} sub={t('lot.s.sc')} span={8}>
            <Bubbles
              pts={all.map((r) => ({ k: r.job.slice(-5), x: r.rev, y: mg(r), z: Math.abs(r.rev - r.cost), warn: mg(r) < 5, c: mg(r) < 0 ? 'pr' : mg(r) < 5 ? 'p2' : 'p1', tip: t('lot.sc.tip', { job: r.job, cus: r.cus, rev: f1(r.rev), m: pc(mg(r)) }) }))}
              xk={t('lot.sc.x')} yk={t('lot.sc.y')} qy={5} fy={(v) => `${v.toFixed(0)}%`}
            />
          </Card>
          <Card title={t('lot.k.ins')} sub={t('lot.s.ins')} span={4}><ul className={s.ins}>{ins.map((x) => <li key={x}>{x}</li>)}</ul></Card>
        </>
      )}
      <Card title={t(low ? 'lot.k.low' : 'lot.k.list')} sub={t(low ? 'lot.s.low' : 'lot.s.list')} span={12}>
        {rows.length ? <Table label={t(low ? 'lot.k.low' : 'lot.k.list')} columns={cols} rows={rows} rowKey={(r) => r.job} dense /> : <p className={s.mute}>{t(low ? 'lot.empty.low' : 'lot.empty.all')}</p>}
      </Card>
    </>
  );
}
